import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';
import { ChevronLeft, ChevronRight, Clock, Zap, Trophy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyLesson() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    fetchDailyLesson();
  }, []);

  const fetchDailyLesson = async () => {
    try {
      const res = await axios.get(`${API}/education/daily-lesson`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLesson(res.data);
    } catch (err) {
      console.error('Failed to fetch daily lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSubmit = async () => {
    if (selectedAnswer === null) return;
    setShowResult(true);
    if (!completed) {
      try {
        const res = await axios.post(
          `${API}/education/daily-lesson/complete`,
          { lesson_id: lesson.id, answer: selectedAnswer },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setXpEarned(res.data.xp_earned || lesson.xp_reward);
        setCompleted(true);
        toast.success(`+${res.data.xp_earned || lesson.xp_reward} XP gagnés !`);
      } catch (err) {
        console.error('Failed to complete daily lesson:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="academie" />

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button onClick={() => navigate('/academie')} className="hover:text-[#ff6b35] transition-colors">Académie</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">Leçon du Jour</span>
        </div>

        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-semibold text-[#ff6b35] uppercase tracking-widest mb-1">{lesson.category} • Leçon du Jour</div>
              <h1 className="text-2xl font-bold text-white">{lesson.title}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-400 mt-2">
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4 text-[#ff6b35]" />
                  +{lesson.xp_reward} XP
                </span>
                <span>• Quiz inclus</span>
              </div>
            </div>
            {completed && (
              <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Complétée</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {!quizStarted ? (
          <>
            <div className="glass rounded-2xl p-8 mb-6">
              <ReactMarkdown
                components={{
                  h2: ({children}) => <h2 className="text-xl font-bold text-[#ff6b35] mb-3">{children}</h2>,
                  p: ({children}) => <p className="text-gray-300 leading-relaxed mb-3">{children}</p>,
                  strong: ({children}) => <strong className="text-white font-bold">{children}</strong>,
                  ul: ({children}) => <ul className="list-disc list-inside text-gray-300 space-y-1 mb-3">{children}</ul>,
                  li: ({children}) => <li className="text-gray-300">{children}</li>,
                  code: ({children}) => <code className="bg-gray-800 text-[#ff6b35] px-2 py-1 rounded text-sm font-mono">{children}</code>,
                }}
              >
                {lesson.content}
              </ReactMarkdown>
            </div>

            <div className="flex justify-between gap-4">
              <Button onClick={() => navigate('/academie')} variant="outline" className="border-gray-700 text-gray-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                data-testid="start-daily-quiz-btn"
                onClick={() => setQuizStarted(true)}
                className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
              >
                Répondre au Quiz →
              </Button>
            </div>
          </>
        ) : (
          /* Quiz */
          <div className="glass rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-6">Question du Jour</h2>
            <p className="text-lg font-semibold text-white mb-6">{lesson.quiz.question}</p>

            <div className="space-y-3 mb-6">
              {lesson.quiz.options.map((option, idx) => {
                let btnClass = 'w-full text-left p-4 rounded-xl border transition-all ';
                if (!showResult) {
                  btnClass += selectedAnswer === idx
                    ? 'border-[#ff6b35] bg-[#ff6b35]/10 text-white'
                    : 'border-gray-700 text-gray-300 hover:border-[#ff6b35]/50 hover:bg-gray-800';
                } else {
                  if (idx === lesson.quiz.correct) {
                    btnClass += 'border-green-400 bg-green-400/10 text-green-400';
                  } else if (idx === selectedAnswer && idx !== lesson.quiz.correct) {
                    btnClass += 'border-red-400 bg-red-400/10 text-red-400';
                  } else {
                    btnClass += 'border-gray-700 text-gray-500';
                  }
                }
                return (
                  <button
                    key={idx}
                    data-testid={`daily-option-${idx}`}
                    className={btnClass}
                    onClick={() => !showResult && setSelectedAnswer(idx)}
                    disabled={showResult}
                  >
                    <span className="font-semibold mr-3 text-gray-500">{String.fromCharCode(65 + idx)}.</span>
                    {option}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${selectedAnswer === lesson.quiz.correct ? 'bg-green-400/10 border border-green-400/30' : 'bg-orange-400/10 border border-orange-400/30'}`}>
                  <p className={`font-semibold mb-1 ${selectedAnswer === lesson.quiz.correct ? 'text-green-400' : 'text-orange-400'}`}>
                    {selectedAnswer === lesson.quiz.correct ? '✓ Bonne réponse !' : '→ La bonne réponse était :'}
                  </p>
                  {selectedAnswer !== lesson.quiz.correct && (
                    <p className="text-white font-medium">{lesson.quiz.options[lesson.quiz.correct]}</p>
                  )}
                </div>

                {xpEarned > 0 && (
                  <div className="flex items-center gap-2 text-[#ff6b35] bg-[#ff6b35]/10 border border-[#ff6b35]/30 rounded-full px-4 py-2 w-fit">
                    <Zap className="w-4 h-4" />
                    <span className="font-bold">+{xpEarned} XP gagnés !</span>
                  </div>
                )}

                <Button
                  data-testid="back-to-academy-btn"
                  onClick={() => navigate('/academie')}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold w-full"
                >
                  Retour à l'Académie
                </Button>
              </div>
            )}

            {!showResult && (
              <Button
                data-testid="submit-daily-answer-btn"
                onClick={handleAnswerSubmit}
                disabled={selectedAnswer === null}
                className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold px-8"
              >
                Valider ma réponse
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
